import { DEFAULT_ONLINE_INTERVAL_SECONDS } from "../models/TrendingModelsV2";
import type { CustomOptions, scopeType } from "../models/TrendingModelsV2";
import {
	DEFAULT_TIME_ZONE_MODE,
	TIME_ZONE_MODES,
	type TimeZoneMode,
} from "./timeZone";

export type ZoomRange = { start: number; end: number };
export type CursorPair = { cursor1: number; cursor2: number };
export type TimeScope = { start: number; end: number; period: scopeType };
export type AggregationMode =
	| "avg"
	| "last"
	| "first"
	| "min"
	| "max"
	| "envelope";

export type TrendingUrlState = {
	viewId: number | null;
	timeScope: TimeScope | null;
	zoom: ZoomRange | null;
	cursors: CursorPair | null;
	customOptions: CustomOptions | null;
	aggregationMode: AggregationMode | null;
	autoRefresh: boolean;
};

export type TrendingUrlSnapshot = {
	viewId: number | null;
	timeScope: TimeScope;
	zoom: ZoomRange | null;
	cursors: CursorPair;
	customOptions: CustomOptions;
	aggregationMode: AggregationMode;
	autoRefresh: boolean;
};

const EMPTY_URL_STATE: TrendingUrlState = {
	viewId: null,
	timeScope: null,
	zoom: null,
	cursors: null,
	customOptions: null,
	aggregationMode: null,
	autoRefresh: false,
};

const VALID_PERIODS: scopeType[] = [
	"1 min",
	"10 min",
	"30 min",
	"1 hour",
	"4 hours",
	"12 hours",
	"1 day",
	"3 days",
	"10 days",
	"custom",
];

type BooleanOptionKey = {
	[K in keyof CustomOptions]: CustomOptions[K] extends boolean ? K : never;
}[keyof CustomOptions];

const OPTION_FLAGS: [BooleanOptionKey, string][] = [
	["showGridLines", "grid"],
	["smoothLines", "smooth"],
	["steppedLines", "stepped"],
	["showLegend", "legend"],
	["showTooltip", "tooltip"],
	["combinedView", "combined"],
	["showToolbox", "toolbox"],
	["showYAxisNames", "yaxis"],
];

const readNumber = (params: URLSearchParams, key: string): number | null => {
	const raw = params.get(key);
	if (raw === null || raw.trim() === "") return null;

	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
};

const readViewId = (params: URLSearchParams): number | null => {
	const viewId = readNumber(params, "view");
	return viewId !== null && Number.isInteger(viewId) ? viewId : null;
};

const readTimeScope = (params: URLSearchParams): TimeScope | null => {
	const start = readNumber(params, "start");
	const end = readNumber(params, "end");
	const period = params.get("period") as scopeType | null;

	if (start === null || end === null || start >= end) return null;
	if (period === null || !VALID_PERIODS.includes(period)) return null;

	return { start, end, period };
};

const readZoom = (params: URLSearchParams): ZoomRange | null => {
	const raw = params.get("zoom");
	if (raw === null) return null;

	const [start, end] = raw.split("-").map(Number);
	if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
	if (start < 0 || end > 100 || start >= end) return null;

	return { start, end };
};

const readCursors = (
	params: URLSearchParams,
	timeScope: TimeScope | null,
): CursorPair | null => {
	if (timeScope === null) return null;

	const cursor1 = readNumber(params, "c1");
	const cursor2 = readNumber(params, "c2");

	if (cursor1 === null || cursor2 === null || cursor1 >= cursor2) return null;
	if (cursor1 < timeScope.start || cursor2 > timeScope.end) return null;

	return { cursor1, cursor2 };
};

const readTimeZoneMode = (params: URLSearchParams): TimeZoneMode => {
	const tzParam = params.get("tz") as TimeZoneMode | null;
	return tzParam !== null && TIME_ZONE_MODES.includes(tzParam)
		? tzParam
		: DEFAULT_TIME_ZONE_MODE;
};

const readCustomOptions = (params: URLSearchParams): CustomOptions | null => {
	const raw = params.get("opts");
	if (raw === null) return null;

	const enabledFlags = new Set(raw.split(",").filter(Boolean));

	const flags = OPTION_FLAGS.reduce(
		(options, [key, flag]) => {
			options[key] = enabledFlags.has(flag);
			return options;
		},
		{} as Record<BooleanOptionKey, boolean>,
	);

	return {
		...flags,
		rawData: false,
		timeZoneMode: readTimeZoneMode(params),
		onlineIntervalSeconds: DEFAULT_ONLINE_INTERVAL_SECONDS,
	};
};

const VALID_AGGREGATION_MODES: AggregationMode[] = [
	"avg",
	"last",
	"first",
	"min",
	"max",
	"envelope",
];

const readAggregationMode = (
	params: URLSearchParams,
): AggregationMode | null => {
	const raw = params.get("agg") as AggregationMode | null;
	return raw !== null && VALID_AGGREGATION_MODES.includes(raw) ? raw : null;
};

export const readTrendingUrlState = (): TrendingUrlState => {
	if (typeof window === "undefined") return EMPTY_URL_STATE;

	const params = new URLSearchParams(window.location.search);
	const timeScope = readTimeScope(params);

	return {
		viewId: readViewId(params),
		timeScope,
		zoom: readZoom(params),
		cursors: readCursors(params, timeScope),
		customOptions: readCustomOptions(params),
		aggregationMode: readAggregationMode(params),
		autoRefresh: params.get("refresh") === "1",
	};
};

const setOrDeleteParam = (
	params: URLSearchParams,
	key: string,
	value: string | null,
) => {
	if (value === null) {
		params.delete(key);
		return;
	}
	params.set(key, value);
};

const roundPercent = (value: number) => Number(value.toFixed(4));

const serializeZoom = (zoom: ZoomRange | null) =>
	zoom === null
		? null
		: `${roundPercent(zoom.start)}-${roundPercent(zoom.end)}`;

const serializeCustomOptions = (customOptions: CustomOptions) =>
	OPTION_FLAGS.filter(([key]) => customOptions[key])
		.map(([, flag]) => flag)
		.join(",");

export const writeTrendingUrlState = (snapshot: TrendingUrlSnapshot) => {
	if (typeof window === "undefined") return;

	const params = new URLSearchParams(window.location.search);

	setOrDeleteParam(
		params,
		"view",
		snapshot.viewId === null ? null : String(snapshot.viewId),
	);
	params.set("start", String(snapshot.timeScope.start));
	params.set("end", String(snapshot.timeScope.end));
	params.set("period", snapshot.timeScope.period);
	setOrDeleteParam(params, "zoom", serializeZoom(snapshot.zoom));
	params.set("c1", String(snapshot.cursors.cursor1));
	params.set("c2", String(snapshot.cursors.cursor2));
	params.set("opts", serializeCustomOptions(snapshot.customOptions));
	setOrDeleteParam(
		params,
		"tz",
		snapshot.customOptions.timeZoneMode === DEFAULT_TIME_ZONE_MODE
			? null
			: snapshot.customOptions.timeZoneMode,
	);
	params.set("agg", snapshot.aggregationMode);
	setOrDeleteParam(params, "refresh", snapshot.autoRefresh ? "1" : null);

	const search = params.toString();
	const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;

	window.history.replaceState(null, "", url);
};
